/**
 * The status-bar selection aggregate, as the UI sees it (Requirement 1.10).
 *
 * The arithmetic is not here. It is `core/query/aggregate.ts` — a pure fold over cell
 * values — and it runs inside the kernel worker, where the Data_Store is: a
 * whole-column selection is 5,000,000 cells, and summing it on the main thread would
 * cost a frame no matter how the reads were batched. This module is the two things the
 * UI needs on its side of the boundary:
 *
 * - {@link SelectionAggregator}, the one-method capability the status bar is written
 *   against, and {@link createKernelAggregator}, which satisfies it by forwarding to
 *   `QueryEngine.aggregate` over the worker's aggregate channel.
 * - {@link formatAggregate}, which turns the five numbers into the status bar's line.
 *
 * The fold's types and helpers are re-exported so a caller has one import for "the
 * aggregate", and Property 6 keeps checking the fold itself rather than this wiring.
 */

import type { AggResult } from '../core/query/aggregate.js';
import type { Range } from '../core/types.js';

export {
  accumulate,
  aggregateCells,
  createAccumulator,
  emptyAggregate,
  finish,
  MAX_AGGREGATE_CELLS,
  type AggAccumulator,
  type AggResult,
} from '../core/query/aggregate.js';

export interface AggregateRequest {
  /** The selected rectangle. Its dimensions are reported as-is. */
  range: Range;
  /**
   * Rectangle worth scanning, usually the sheet's used range. Cells outside it are empty
   * by definition, so clipping to it is free of consequence and turns a whole-column
   * selection into a small read.
   */
  bounds?: Range | null;
}

/**
 * What the status bar asks for.
 *
 * Deliberately narrow: one method, one request, one result. Naming the capability rather
 * than the implementation is what let the status bar be written before the Query_Engine
 * existed and stay unchanged when it arrived.
 */
export interface SelectionAggregator {
  aggregate(request: AggregateRequest): Promise<AggResult>;
}

/** The worker's aggregate channel, as this module needs it. `KernelClient` satisfies it. */
export interface AggregateSource {
  aggregate(range: Range, bounds?: Range | null): Promise<AggResult>;
}

/**
 * The aggregator the App Shell uses: one round trip to the Query_Engine
 * (Requirements 1.10, 13.9).
 *
 * One message per selection change, whatever the selection's size — the worker clips to
 * the used range and folds over columnar storage, so a click on a column header costs
 * the same message a click on one cell does.
 */
export function createKernelAggregator(source: AggregateSource): SelectionAggregator {
  return {
    aggregate(request: AggregateRequest): Promise<AggResult> {
      return source.aggregate(request.range, request.bounds ?? null);
    },
  };
}

/** The status bar's text for one aggregate. */
export function formatAggregate(agg: AggResult): string {
  const parts = [`Rows: ${agg.rows}`, `Cols: ${agg.cols}`, `Count: ${agg.nonEmpty}`];
  if (agg.numeric > 0) {
    parts.push(`Sum: ${trim(agg.sum)}`);
    if (agg.average !== null) parts.push(`Average: ${trim(agg.average)}`);
  }
  if (!agg.complete) parts.push(`(first ${agg.scanned} cells)`);
  return parts.join('  ·  ');
}

/** Shortens a number for display without pretending to be the Format_Manager. */
function trim(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 1e6) / 1e6);
}
